import { LongHaulTrip } from './types';

/**
 * 運行 1 本を 1 行で言い表す文言。バーが狭いときのホバー（`TripBar`）と、掴んでいる間の
 * オーバーレイ（`LongHaulDragOverlayCard`）で共有する。
 *
 * Why not それぞれで組み立てる: 同じ「この運行が何か」を 2 か所で書くと、片方にだけ
 * 項目を足したときに、掴む前と掴んだあとで別の運行に見える。
 */
export function longHaulTripSummary(trip: LongHaulTrip): string {
  const route = [trip.loadingName, ...trip.unloadingNames].filter(Boolean).join(' → ');
  // 都道府県は両端そろわないと区間にならない（片側だけでは「どこから」が読めない）
  const prefectures = trip.loadingPrefecture && trip.unloadingPrefectures.length > 0
    ? [trip.loadingPrefecture, ...trip.unloadingPrefectures].join(' → ')
    : null;
  return [trip.shipperName, route, prefectures].filter(Boolean).join(' / ');
}

/**
 * DragOverlay に出すもの。掴んでいるのが未割当カードなら id で引き直して実物のカードを、
 * 運行バーなら 1 行の文言を出す。
 *
 * Why not 掴んだ `UnassignedItem` をそのまま持つ: ドラッグ中もサーバ再取得や楽観反映で
 * 一覧は入れ替わる。掴んだ時点の実体を抱えると、消えた案件のカードが浮いたまま残る。
 */
export type LongHaulDragOverlayContent = {
  /** 未割当カードを掴んでいるならその id。運行バーなら null */
  itemId: string | null;
  /** 運行バーを掴んでいるときの文言。未割当なら null */
  label: string | null;
  /** まとめて動かしている件数。1 ならバッジを出さない */
  count: number;
};

/**
 * 掴んだものから、オーバーレイに出す内容を決める。ボード以外の draggable
 * （左カラムの車両並べ替え）を掴んだときは null を返して何も出さない。
 */
export function resolveDragOverlayContent(input: {
  activeId: string;
  /** `readTripDragData` の結果。運行バーを掴んでいれば入る */
  trip: LongHaulTrip | null;
  /** 掴んだ id が未割当一覧にあるか */
  isUnassignedItem: boolean;
  selectedUnassignedIds: ReadonlySet<string>;
}): LongHaulDragOverlayContent | null {
  const {
    activeId, trip, isUnassignedItem, selectedUnassignedIds,
  } = input;

  if (trip) return { itemId: null, label: longHaulTripSummary(trip), count: 1 };
  if (!isUnassignedItem) return null;

  // 選択の外のカードを掴んだときに動くのは 1 枚だけ（resolveDraggedUnassigned と同じ判定）
  const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
  return { itemId: activeId, label: null, count: isMulti ? selectedUnassignedIds.size : 1 };
}
